/**
 * ContinuityStrip — greets the user on Today with the Navigator's memory of
 * them before they say a word: the commitment they made last session (their own
 * words), the 1-2 core takeaways, and the assignment that's due.
 *
 * Pure read — sourced from the most recent *coaching* session's `state_delta`
 * (commitment + core_takeaways, the same fields opener.py follows up on) and the
 * active assignment, both already in the /state payload. No AI calls.
 *
 * Only anchors on real coaching sessions (session_type 'chat'); planning,
 * review, check-in, and onboarding sessions can carry a coaching-style summary
 * but must never be quoted back as "what you committed to". Tone follows
 * drive_level — softer framing at Coach level (consistent with the other Today panels).
 */

import { History, ClipboardCheck } from 'lucide-react';

// Match opener.py's follow-up horizon in spirit (a touch more generous, since
// this is passive context rather than an active nudge).
const STALE_DAYS = 14;

function startOfDayMs(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Calendar-day difference (matches dueBadge's day definition for coherence).
function calendarDaysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d)) return Infinity;
  return Math.round((startOfDayMs(new Date()) - startOfDayMs(d)) / 86400000);
}

function agoLabel(days) {
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return 'over a week ago';
}

// session_type defaults to 'chat'; treat missing as coaching, exclude the rest.
function isCoaching(session) {
  const st = session && session.session_type;
  return !st || st === 'chat';
}

// state_delta arrives as a nested object today; tolerate a JSON string too,
// mirroring opener.py's reader.
function parseDelta(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return null;
}

// Most recent coaching session carrying a real anchor (commitment or takeaway).
function pickAnchor(sessions) {
  for (const s of sessions || []) {
    if (!isCoaching(s)) continue;
    const delta = parseDelta(s && s.state_delta);
    if (delta && typeof delta === 'object') {
      const commitment = typeof delta.commitment === 'string' ? delta.commitment.trim() : '';
      const takeaways = Array.isArray(delta.core_takeaways)
        ? delta.core_takeaways.filter(t => typeof t === 'string' && t.trim()).slice(0, 2)
        : [];
      if (commitment || takeaways.length) {
        return { session: s, commitment, takeaways };
      }
    }
  }
  return null;
}

function dueBadge(deadline, gentle) {
  if (!deadline) return { text: 'open', cls: 'text-slate-400' };
  const d = new Date(deadline);
  if (isNaN(d)) return { text: 'open', cls: 'text-slate-400' };
  const diff = Math.round((startOfDayMs(d) - startOfDayMs(new Date())) / 86400000);
  if (diff < 0) return { text: `overdue ${-diff}d`, cls: gentle ? 'text-amber-400' : 'text-red-400' };
  if (diff === 0) return { text: 'due today', cls: 'text-amber-400' };
  if (diff === 1) return { text: 'due tomorrow', cls: 'text-amber-400' };
  return { text: `due in ${diff}d`, cls: 'text-slate-400' };
}

export default function ContinuityStrip({ sessions, activeAssignment, driveLevel = 'mentor', className = '' }) {
  const gentle = driveLevel === 'coach';

  const anchor = pickAnchor(sessions);
  const anchorAge = anchor ? calendarDaysSince(anchor.session.ended_at || anchor.session.started_at) : Infinity;
  const anchorFresh = anchor && anchorAge <= STALE_DAYS;
  const due = activeAssignment && activeAssignment.goal_text ? activeAssignment : null;

  if (!anchorFresh && !due) return null;

  const badge = due ? dueBadge(due.deadline, gentle) : null;
  const title = gentle ? 'Picking up where you were' : 'Where you left off';
  const commitCaption = gentle ? '— where you wanted to head next' : '— what you committed to';

  return (
    <section
      aria-label={title}
      className={`rounded-lg border border-slate-700/50 bg-slate-800/40 shadow-soft p-4 ${className}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <History size={14} className="text-blue-400 flex-shrink-0" />
        <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</h3>
        {anchorFresh && (
          <span className="text-[11px] text-slate-600 ml-auto">{agoLabel(anchorAge)}</span>
        )}
      </div>

      {anchorFresh && anchor.commitment && (
        <blockquote className="border-l-2 border-blue-500/50 pl-3 mb-2">
          <p className="text-sm text-slate-200 italic leading-snug">&ldquo;{anchor.commitment}&rdquo;</p>
          <p className="text-[11px] text-slate-500 mt-1">{commitCaption}</p>
        </blockquote>
      )}

      {anchorFresh && anchor.takeaways.length > 0 && (
        <p className="text-xs text-slate-400 leading-snug">
          <span className="text-slate-500">Last time: </span>
          {anchor.takeaways.join('; ')}
        </p>
      )}

      {due && (
        <div className={`flex items-center gap-2 mt-3 ${anchorFresh ? 'pt-3 border-t border-slate-700/40' : ''}`}>
          <ClipboardCheck size={14} className="text-slate-400 flex-shrink-0" />
          <span className="text-sm text-slate-300 truncate flex-1 min-w-0">{due.goal_text}</span>
          <span className={`text-[11px] font-medium flex-shrink-0 ${badge.cls}`}>{badge.text}</span>
        </div>
      )}
    </section>
  );
}
